import { useContext, useEffect, useState } from 'react';
import { differenceBy } from 'lodash';
import { alephToast, NotificationToast, useUrlParams } from '@aleph/shared-components';
import useNotificationsPolling
	from 'wb-reddit/app/routes/superGrid/actionRow/notifications/hooks/useNotificationsPolling';
import { RedditNotificationsQueryItems } from 'wb-reddit/graphql/generated';
import {
	getUrlParamsAndValidationsFromNotification,
	updateEntityFromNotification,
} from 'wb-reddit/app/routes/superGrid/actionRow/notifications/util';
import { useSuperGridValidate } from '@aleph/supergrid';
import { useApolloClient } from '@apollo/client';
import { RedditSuperGridContext } from 'wb-reddit/app/routes/superGrid/utils';
import { captureException } from '@sentry/react';
import { useNavigate, useParams } from 'react-router-dom';

const useSnackbarNotifications = (disableInspect?: boolean) => {
	const client = useApolloClient();
	const redditSuperGridContext = useContext(RedditSuperGridContext);
	const [prevNotifications, setPrevNotifications] = useState<RedditNotificationsQueryItems[]>();

	const { notificationsLoaded, notifications, loading: loadingNotifications } = useNotificationsPolling();
	const navigate = useNavigate();

	const { params, setUrlParams } = useUrlParams({ navigate, useParams });
	const { context: { setValidations } } = useSuperGridValidate();

	useEffect(() => {
		if (loadingNotifications) {
			return;
		}

		const unreadNotifications = notifications
			?.filter((notification) => !notification.notificationIsRead) || [];

		if (prevNotifications && notificationsLoaded) {
			const newNotifications = differenceBy(unreadNotifications, prevNotifications ?? [], 'notificationId');

			if (redditSuperGridContext?.gridApi) {
				try {
					updateEntityFromNotification(newNotifications, client, redditSuperGridContext.gridApi);
				} catch (e) {
					captureException(e);
				}
			}

			if (newNotifications.length > 0) {
				newNotifications.forEach((notification) => {
					const { urlParams, validations } = getUrlParamsAndValidationsFromNotification(notification);

					alephToast(
						({ closeToast }) => (
							<NotificationToast
								type={notification.notificationType}
								title={notification.notificationTitle}
								description={notification.notificationMessage}
								action={
									(urlParams && !disableInspect) ? <button
										onClick={() => {
											closeToast?.();
											setUrlParams({
												...params,
												...urlParams,
											});
											setValidations(validations);
										}}
									>
										Inspect
									</button> : undefined
								}
								closeToast={closeToast}
							/>
						),
						{
							autoClose: 10000,
						},
					);
				});
			}
		}

		setPrevNotifications(unreadNotifications);
	}, [notifications, loadingNotifications]);
};

export default useSnackbarNotifications;
